import { parse } from "@babel/parser";
import type { Node } from "@babel/types";
import { getSelectedNodeId } from "@builder/assist/services/tools/helpers";
import { getCurrentLayer } from "@builder/store/slices/BuilderSlice/builderHelpers";

import { BaseLayer, BuildComponentKey, BuildNode } from "@common/build/types";
import { _lowerCaseFirstLetter, buildJSXFromNode, convertJSXTextToTextComponent, getElementName, JSXElementToBuilderNode, unimplementedNodeJsxToBuilder, validateChildInParent } from "./jsxHelpers";
import { JSX_NODE_MAPPINGS } from "./jsxMapping";


export function getCleanJSX(layer: BaseLayer, key?: string) {
  key = key || layer.root;

  const _recursive_renderNodesJSXString = (key: string): string => {
    const node = layer.components[key];
    const { children } = node;

    // First build children JSX (needed even for custom renderers)
    let childrenRender = "";
    if (children && children.length > 0) {
      childrenRender = children
        .map((childId: string) => _recursive_renderNodesJSXString(childId))
        .filter(child => child.length > 0)
        .join("");
    }

    const jsxString = buildJSXFromNode(node, childrenRender);
    return jsxString;
  };

  return _recursive_renderNodesJSXString(key);
}

export function convertJSXtoNodes(
  jsxString: string,
  targetParentID: string,
  options?: { fromNodes?: BuildNode[], canThrowError?: boolean },
  
) {
  const allNodes: BuildNode[] = [];
  const nodeMap: Record<string, BuildNode> = {};

  try {
    const ast = parse(jsxString, {
      sourceType: "module",
      plugins: ["jsx", "typescript"],
    });

    const traverse = (node: Node, parentId: string | null): BuildNode | null => {
      if (!node) return null;

      if (node.type === "JSXElement") {
        const nodeName = getElementName(node)
        const isImplemented = !!JSX_NODE_MAPPINGS[_lowerCaseFirstLetter(nodeName || "") as BuildComponentKey];
        const nodes = (nodeName && isImplemented) ? JSXElementToBuilderNode(node, parentId, options?.fromNodes) : unimplementedNodeJsxToBuilder(node, parentId, options?.fromNodes);
        if (nodes && nodes.length) {
          allNodes.push(...nodes);
          nodes.forEach(n => { nodeMap[n.id] = n; });
          const rootNode = nodes[0];

          // Determine if we need to skip traversing children for this component
          let mappingForRoot = JSX_NODE_MAPPINGS[rootNode.name as BuildComponentKey];
          
          const skipChildren = !!mappingForRoot?.ignoreJSXChildren;
          // Process children and establish parent-child relationships (unless instructed to skip)
          if (!skipChildren && node.children) {
            const childNodes: BuildNode[] = [];
            node.children.forEach((child: any) => {
              const childNode = traverse(child, rootNode.id);
              if (childNode) {
                validateChildInParent(childNode, rootNode, nodeMap)

                childNodes.push(childNode);
              }
            });
            if (childNodes.length > 0) {
              rootNode.children = childNodes.map(c => c.id);
            }
          }

          // Run any custom validation provided by the mapper
          if (mappingForRoot?.customValidate) {
            try {
              mappingForRoot?.customValidate(rootNode, nodeMap);
            } catch (e) {
              console.error("customValidate failed, rejecting with error:", e);
              throw e;
            }
          }

          return rootNode;
        }
      } else if (node.type === "JSXFragment") {
        // For JSXFragment, process children directly without creating a wrapper node
        if (node.children) {
          const childNodes: BuildNode[] = [];
          node.children.forEach((child: any) => {
            const childNode = traverse(child, parentId);
            if (childNode) {
              childNodes.push(childNode);
            }
          });

          // Return the first child node as the representative node
          return childNodes.length > 0 ? childNodes[0] : null;
        }
      } else if (node.type === "JSXText") {
        const textElement = convertJSXTextToTextComponent(node);
        if (!textElement) return null;
        const nodes = JSXElementToBuilderNode(textElement, parentId, options?.fromNodes);
        if (nodes && nodes.length) {
          allNodes.push(...nodes);
          nodes.forEach(n => { nodeMap[n.id] = n; });
          return nodes[0];
        }
      } else {
        // For other node types, try to find the first valid child
        const values = Object.values(node);
        for (const value of values) {
          if (Array.isArray(value)) {
            for (const child of value) {
              const result = traverse(child, parentId);
              if (result) return result;
            }
          } else if (value && typeof value === "object" && (value as any).type) {
            const result = traverse(value as any, parentId);
            if (result) return result;
          }
        }
      }

      return null;
    };

    for (const node of (ast as any).program.body) {
      const result = traverse(node as any, targetParentID);
      if (result) return { rootNode: result, allNodes };
    }
  } catch (error) {
    if (options?.canThrowError) {
      throw error;
    }
  }

  return { rootNode: null, allNodes: [] };
}



//dev function: get clean jsx by customname:
export function devGetJSX(customNameOrId?: string) {
  const layer = getCurrentLayer().layer
  
  if (!customNameOrId ) {
    const selectedNodeId = getSelectedNodeId();
    if (selectedNodeId) {
      return getCleanJSX(layer,selectedNodeId);
    }
    return getCleanJSX(layer);
  }
  if (layer.components[customNameOrId]) {
    return getCleanJSX(layer,customNameOrId);
  }
  else {
    const node = Object.values(layer.components).find(node => node.customName === customNameOrId);
    if (!node) return getCleanJSX(layer,layer.root);
    return getCleanJSX(layer,node.id);
  }

}
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.getJSX = (stringify?: boolean = false)=>console.log(stringify ? JSON.stringify(devGetJSX()) : devGetJSX())

